import React, { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CommandView from '../components/command/CommandView';
import useTelemetry, { TelemetryControls, ActivityStrip } from '../components/command/useTelemetry';
import { trafficPanel, anomalyPanel, AnomalyTable, FlowTable, RuntimeRows } from '../components/command/telemetryHud';
import { deriveFleet, fleetInspector } from '../components/three/fleetState';
import { ago } from '../components/three/ecosystemState';
import { apiGet } from '../utils/backendApi';
import { FLEET_ASPECT, fleetLive } from './live/routes';

// Operator fleet command view: the whole fleet on one stage, HUD totals that
// equal the Costs / Health / Hosts tabs, and a drawer per instance that
// carries the same row + actions the Instances tab renders.
const usd = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`);
const Row = ({ k, v }) => <span className="cmd-row"><span>{k}</span><span>{v}</span></span>;
const Basis = ({ b }) => <span className={`cmd-basis ${b === 'actual' ? 'is-actual' : ''}`}>{b}</span>;

// Standby runner pool (backend shared/standby.js → ops/standby): warm
// browser/job tasks that make Lab tools start in seconds. Level: no pool
// configured → off; a cold start or waiting job → warn; runners busy → busy.
const standbyLevel = (sb) => {
  const fams = Object.values(sb?.families || {}).filter((f) => f.idle > 0);
  if (!fams.length) return 'off';
  if (fams.some((f) => f.waiting > 0 || f.idleNow + f.pending < f.idle)) return 'warn';
  if (fams.some((f) => f.busy > 0)) return 'busy';
  return 'ok';
};

export default function AdminFleetCommand({
  instances, incidents, costData, hosts, standby, pinnedKitRef, busy, onAction, onRevokeLicense, onScan, InstanceRow, chrome = 150,
}) {
  const navigate = useNavigate();
  const openFleet = useCallback((id) => {
    if (!FLEET_ASPECT[id]) return false;
    navigate(fleetLive(FLEET_ASPECT[id]));
    return true;
  }, [navigate]);
  const data = useMemo(() => (instances ? { mode: 'admin', instances, incidents: incidents || [], costs: costData, hosts: hosts || [], pinnedKitRef } : null), [instances, incidents, costData, hosts, pinnedKitRef]);
  const s = useMemo(() => deriveFleet(data), [data]);
  const period = costData?.period || '';
  const plane = costData?.plane || null;
  const t = s.totals;
  const overhead = plane && plane.totalUsd > t.appsMtdUsd ? plane.totalUsd - t.appsMtdUsd : null;

  // Live telemetry (edge access-log rollups + plane calls) or the simulator
  // over the same roster. Both go through one shape; the stage never knows.
  const roster = useMemo(() => s.nodes.map((n) => ({ slug: n.slug, pool: n.pool, external: n.external, envRole: n.envRole, twinSlug: (instances || []).some((i) => i.slug === `${n.slug}-uat`) ? `${n.slug}-uat` : null })), [s.nodes, instances]);
  const fetchReal = useCallback((w) => apiGet(`/apps/admin/telemetry?window=${w}`), []);
  const { telemetry, controls } = useTelemetry({ surface: 'fleet', roster, fetchReal, enabled: !!data });
  // Stage cards re-derive from the current fleet + telemetry on every poll.
  const inspectorFor = useCallback((sel) => fleetInspector(s, telemetry, sel), [s, telemetry]);

  const panels = useMemo(() => (!data ? [] : [
    {
      id: 'plane', side: 'left', level: plane ? 'gold' : 'off', title: `Control plane · ${period}`,
      body: plane ? (
        <>
          <strong>{usd(plane.totalUsd)}<span className="cmd-sub"> mtd</span><Basis b="actual" /></strong>
          <Row k="burn / day" v={usd(plane.burnPerDayUsd)} />
          <Row k="projected" v={usd(plane.projectedUsd)} />
          <Row k="attributed to apps" v={usd(t.appsMtdUsd)} />
          {overhead != null && <Row k="overhead" v={usd(overhead)} />}
          <Row k="app margin" v={usd(t.revenueUsd - t.appsMtdUsd)} />
          {plane.pulledAt ? <span className="cmd-sub">pulled {ago(plane.pulledAt)}</span> : null}
        </>
      ) : <span className="cmd-sub">No plane cost rollup yet — the hourly rollup writes it once Cost Explorer has tagged data.</span>,
    },
    {
      id: 'fleet', side: 'left', level: t.down ? 'down' : t.provisioning ? 'busy' : 'ok', title: 'Fleet',
      body: (
        <>
          <strong>{t.live}<span className="cmd-sub"> live · {t.instances} total</span></strong>
          <Row k="provisioning" v={t.provisioning} />
          <Row k="down" v={t.down} />
          <Row k="degraded" v={s.legend.find((l) => l.key === 'warn')?.count || 0} />
          <Row k="idle (30d)" v={t.idle} />
          <Row k="external" v={s.external.length} />
        </>
      ),
    },
    {
      id: 'hosts', side: 'left', level: t.hosts ? (t.hostsOk === t.hosts ? 'ok' : 'warn') : 'off', title: 'Hosts',
      body: (
        <>
          <strong>{t.hostsOk}/{t.hosts}<span className="cmd-sub"> healthy</span></strong>
          {s.hosts.slice(0, 4).map((h) => <Row key={h.id} k={`…${String(h.id).slice(-6)}`} v={`${h.count}/${h.capacity}${h.memUsedPct != null ? ` · ${h.memUsedPct}% mem` : ''}`} />)}
        </>
      ),
    },
    {
      id: 'incidents', side: 'right', level: t.incidents ? 'down' : 'ok', title: 'Incidents',
      body: (
        <>
          <strong>{t.incidents}<span className="cmd-sub"> open</span></strong>
          {(incidents || []).filter((i) => i.status === 'open').slice(0, 4).map((i) => <Row key={i.id} k={i.slug} v={ago(i.openedAt)} />)}
        </>
      ),
    },
    {
      id: 'scans', side: 'right', level: t.findingsHigh ? 'warn' : t.scanning ? 'busy' : 'ok', title: 'Security',
      body: (
        <>
          <strong>{t.scanning ? `${t.scanning} scanning` : `${t.findingsHigh} with high+`}</strong>
          {s.nodes.filter((n) => n.scanLevel === 'down' || n.scanLevel === 'warn').slice(0, 4).map((n) => <Row key={n.id} k={n.slug} v={n.scanning ? 'running' : n.scanLevel === 'down' ? 'critical' : 'high'} />)}
        </>
      ),
    },
    {
      id: 'release', side: 'right', level: t.drift ? 'warn' : 'ok', title: 'Release',
      body: (
        <>
          <strong>{pinnedKitRef || 'main'}<span className="cmd-sub"> pinned</span></strong>
          <Row k="off the pin" v={t.drift} />
          {s.nodes.filter((n) => n.kitDrift).slice(0, 3).map((n) => <Row key={n.id} k={n.slug} v={String(n.kitRef).slice(0, 8)} />)}
        </>
      ),
    },
    {
      id: 'standby', side: 'right', level: standbyLevel(standby), title: 'Standby runners',
      body: standby ? (
        <>
          <strong>{Object.values(standby.families || {}).reduce((n, f) => n + f.idleNow, 0)}<span className="cmd-sub"> warm · {Object.values(standby.families || {}).reduce((n, f) => n + f.busy, 0)} busy</span></strong>
          {Object.entries(standby.families || {}).filter(([, f]) => f.idle > 0 || f.busy || f.idleNow).map(([k, f]) => (
            <Row key={k} k={k} v={`${f.idleNow}/${f.idle} idle${f.pending ? ` · ${f.pending} starting` : ''}${f.busy ? ` · ${f.busy} busy` : ''}${f.waiting ? ` · ${f.waiting} waiting` : ''}`} />
          ))}
          {Object.values(standby.families || {}).some((f) => f.sinceBoot?.coldStarts) && <Row k="cold starts" v={Object.values(standby.families || {}).reduce((n, f) => n + (f.sinceBoot?.coldStarts || 0), 0)} />}
          {standby.at ? <span className="cmd-sub">swept {ago(standby.at)}</span> : null}
        </>
      ) : <span className="cmd-sub">No standby report yet — the orchestrator writes one every 15 s.</span>,
    },
    trafficPanel(telemetry, { side: 'right' }),
    anomalyPanel(telemetry, { side: 'right' }),
  ].map((p) => (FLEET_ASPECT[p.id] ? { ...p, onOpen: () => openFleet(p.id) } : p))), [data, plane, period, t, overhead, s, incidents, pinnedKitRef, telemetry, standby, openFleet]);

  const drawer = useMemo(() => {
    const out = {
      plane: {
        title: `Control plane costs · ${period}`,
        render: () => (
          <div className="cs-plan-tablewrap">
            <table>
              <thead><tr><th>Service</th><th>MTD</th></tr></thead>
              <tbody>{Object.entries(plane?.byService || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => <tr key={k}><td>{k}</td><td>{usd(v)}</td></tr>)}</tbody>
            </table>
          </div>
        ),
      },
      incidents: {
        title: 'Incidents',
        render: () => (
          <div className="cs-plan-tablewrap">
            <table>
              <thead><tr><th>App</th><th>Status</th><th>Opened</th><th>Reason</th></tr></thead>
              <tbody>{(incidents || []).map((i) => <tr key={i.id}><td>{i.slug}</td><td>{i.status}</td><td>{ago(i.openedAt)}</td><td>{i.reason || '—'}</td></tr>)}</tbody>
            </table>
          </div>
        ),
      },
      traffic: { title: `Inter-app flows · ${telemetry.window.label}`, render: () => <FlowTable t={telemetry} /> },
      anomalies: { title: `Anomalies · ${telemetry.window.label}`, render: () => <AnomalyTable t={telemetry} /> },
      hosts: {
        title: 'Hosts',
        render: () => (
          <div className="cs-plan-tablewrap">
            <table>
              <thead><tr><th>Host</th><th>Status</th><th>Apps</th><th>Mem</th><th>Disk</th><th>Region</th></tr></thead>
              <tbody>{s.hosts.map((h) => <tr key={h.id}><td className="aa-mono">{h.id}</td><td>{h.status}</td><td>{h.count}/{h.capacity}</td><td>{h.memUsedPct != null ? `${h.memUsedPct}%` : '—'}</td><td>{h.diskPct != null ? `${h.diskPct}%` : '—'}</td><td>{h.region}</td></tr>)}</tbody>
            </table>
          </div>
        ),
      },
      standby: {
        title: 'Standby runners',
        render: () => (
          <div className="cs-plan-tablewrap">
            <table>
              <thead><tr><th>Family</th><th>Runner</th><th>Status</th><th>Job</th><th>Code</th><th>Up</th><th>Jobs</th></tr></thead>
              <tbody>
                {Object.entries(standby?.families || {}).flatMap(([k, f]) => (f.runners?.length ? f.runners : [null]).map((r, i) => (
                  <tr key={`${k}-${r?.id || i}`}>
                    <td>{k}{i === 0 ? <span className="cmd-sub"> · target {f.target}/{f.max}{f.sinceBoot ? ` · ${f.sinceBoot.launched} launched · ${f.sinceBoot.retired} retired · ${f.sinceBoot.coldStarts} cold` : ''}</span> : null}</td>
                    {r ? (
                      <>
                        <td className="aa-mono">{String(r.id).slice(0, 8)}</td>
                        <td>{r.retire ? `${r.status} → retiring` : r.status}</td>
                        <td className="aa-mono">{r.jobId || '—'}</td>
                        <td className="aa-mono">{r.code ? `${String(r.code).slice(0, 8)}${standby.code && r.code !== standby.code ? ' (stale)' : ''}` : 'baked'}</td>
                        <td>{r.bootAt ? ago(r.bootAt) : '—'}</td>
                        <td>{r.jobsDone}</td>
                      </>
                    ) : <td colSpan={6} className="cmd-sub">{f.idle > 0 ? `none running yet (${f.pending} starting)` : 'pool off'}</td>}
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        ),
      },
    };
    for (const inst of instances || []) {
      const id = `${inst.orgId || ''}/${inst.appId}`;
      const n = s.nodes.find((x) => x.id === id);
      out[id] = {
        title: inst.name || inst.slug,
        render: () => (
          <>
            {n && (
              <div className="cmd-drawer-stats">
                <Row k="status" v={`${inst.status}${n.monitorLevel !== 'off' ? ` · ${n.monitorLevel}` : ''}`} />
                <Row k="cost mtd / projected" v={`${usd(n.mtdUsd)} / ${usd(n.projectedUsd)}`} />
                <Row k="burn / day" v={usd(n.burnPerDayUsd)} />
                <Row k="margin" v={usd(n.marginUsd)} />
                <Row k="kit" v={`${n.kitRef ? String(n.kitRef).slice(0, 8) : '—'}${n.kitDrift ? ` (pinned ${String(pinnedKitRef).slice(0, 8)})` : ''}`} />
                <Row k="last activity" v={n.lastActivityAt ? ago(n.lastActivityAt) : '—'} />
                <Row k="scan" v={n.scanning ? 'running' : n.scanLevel === 'off' ? 'none' : n.scanLevel} />
                {telemetry.live[inst.slug] && <Row k={`traffic now${telemetry.source === 'sim' ? ' (sim)' : ''}`} v={`${Math.round(telemetry.live[inst.slug].req)}/min · ${telemetry.live[inst.slug].s5xx} 5xx · p95 ${Math.round(telemetry.live[inst.slug].p95)} ms`} />}
                <RuntimeRows t={telemetry} slug={inst.slug} />
              </div>
            )}
            {telemetry.anomalies.filter((a) => a.slug === inst.slug && a.closedAt == null).length > 0 && <AnomalyTable t={{ anomalies: telemetry.anomalies.filter((a) => a.slug === inst.slug) }} />}
            <div className="cmd-rail-actions" style={{ margin: '12px 0' }}>
              {inst.orgId && <Link className="cs-btn cs-btn-sm" to={`/orgs/${inst.orgId}/apps/${inst.appId}`}>Open app command view</Link>}
              {inst.status === 'live' && inst.urls?.app && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => onScan(inst, 'passive')}>Run baseline scan</button>}
            </div>
            <table className="aa-table">
              <thead><tr><th>App</th><th>Slug</th><th>Plan</th><th>Status</th><th>Target</th><th>License</th><th>Host</th><th>Prepaid</th><th /></tr></thead>
              <tbody><InstanceRow inst={inst} onAction={onAction} onRevokeLicense={onRevokeLicense} busy={busy} /></tbody>
            </table>
          </>
        ),
      };
    }
    return out;
  }, [instances, s, plane, period, incidents, busy, onAction, onRevokeLicense, onScan, pinnedKitRef, telemetry, standby]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CommandView
      data={data}
      derive={deriveFleet}
      scene="fleet"
      panels={panels}
      drawer={drawer}
      events={s.events}
      caption={data ? s.caption : 'loading fleet…'}
      chrome={chrome}
      className="is-fleet"
      telemetry={telemetry}
      legend={s.legend}
      defaultInspector={inspectorFor(null)}
      inspectorFor={inspectorFor}
      roomHref="/admin/fleet/room"
      onOpenDetail={(id) => {
        if (openFleet(id)) return true;
        const [orgId, appId] = String(id).split('/');
        if (orgId && appId) { navigate(`/orgs/${orgId}/apps/${appId}`); return true; }
        return false;
      }}
    >
      <TelemetryControls controls={controls} />
      <ActivityStrip telemetry={telemetry} />
    </CommandView>
  );
}
